// Data + audio engine for the /synth-lab synth practice page.
//
// A small classic-subtractive synth (one oscillator → lowpass filter → ADSR
// amp envelope) built on the native Web Audio API — no audio library, same
// lightweight posture as keyboardPlan.js / Timer.js. The page uses this engine
// for four practice modes (free play, match-the-sound, name-that-control,
// one-knob challenge), so the parameter model, scoring, presets and challenge
// data all live here as structured, testable data.

/* ============================================================
   Parameter model
   ============================================================ */

// The waveform choices for the single oscillator. `value` is the string the
// Web Audio OscillatorNode understands; the rest is teaching copy.
export const WAVEFORMS = [
  { value: "sine", label: "Sine", blurb: "Pure and round — just the fundamental, no harmonics." },
  { value: "triangle", label: "Triangle", blurb: "Soft and hollow, like a gentle flute." },
  { value: "sawtooth", label: "Saw", blurb: "Bright and buzzy — every harmonic. The classic synth string/brass tone." },
  { value: "square", label: "Square", blurb: "Hollow and reedy — odd harmonics only, a bit like a clarinet." },
];

export const WAVEFORM_VALUES = WAVEFORMS.map((w) => w.value);

// The continuous knobs. Each carries its range, a default, the unit, the bit of
// terminology it teaches (`concept`), a one-line `blurb`, a `format` for the
// readout, and a `scale` ("log" | "linear") used when normalizing for scoring
// so that perceptually-even moves count evenly.
export const PARAMS = [
  {
    id: "cutoff",
    label: "Cutoff",
    concept: "Filter cutoff",
    min: 80,
    max: 8000,
    step: 1,
    default: 1800,
    unit: "Hz",
    scale: "log",
    blurb: "How much brightness the lowpass filter lets through. Low = dark/muffled, high = bright/open.",
    format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`) + " Hz",
  },
  {
    id: "resonance",
    label: "Resonance",
    concept: "Filter resonance (Q)",
    min: 0.5,
    max: 18,
    step: 0.1,
    default: 1,
    unit: "Q",
    scale: "linear",
    blurb: "Emphasis right at the cutoff frequency. Crank it for a whistly, vowel-like peak.",
    format: (v) => v.toFixed(1),
  },
  {
    id: "attack",
    label: "Attack",
    concept: "Envelope attack",
    min: 0.001,
    max: 2,
    step: 0.001,
    default: 0.01,
    unit: "s",
    scale: "log",
    blurb: "How long the note takes to fade in. Tiny = plucky/percussive, long = a slow swell.",
    format: (v) => `${(v * 1000).toFixed(0)} ms`,
  },
  {
    id: "decay",
    label: "Decay",
    concept: "Envelope decay",
    min: 0.01,
    max: 2.5,
    step: 0.01,
    default: 0.25,
    unit: "s",
    scale: "log",
    blurb: "How quickly it falls from the initial peak down to the sustain level.",
    format: (v) => `${(v * 1000).toFixed(0)} ms`,
  },
  {
    id: "sustain",
    label: "Sustain",
    concept: "Envelope sustain",
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.7,
    unit: "",
    scale: "linear",
    blurb: "The level the note holds at while a key is down. Low = it dies away, high = it stays loud.",
    format: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: "release",
    label: "Release",
    concept: "Envelope release",
    min: 0.01,
    max: 3,
    step: 0.01,
    default: 0.3,
    unit: "s",
    scale: "log",
    blurb: "How long the note keeps ringing after you let go. Short = abrupt, long = a lingering tail.",
    format: (v) => `${(v * 1000).toFixed(0)} ms`,
  },
];

export const PARAM_BY_ID = PARAMS.reduce((m, p) => {
  m[p.id] = p;
  return m;
}, {});

// A short glossary keyed for the page's "what the words mean" panel. Waveform
// and the six knobs, each as { term, concept, blurb }.
export const GLOSSARY = [
  { term: "Waveform", blurb: "The raw shape of the oscillator's tone before any filtering. More harmonics = brighter and buzzier." },
  ...PARAMS.map((p) => ({ term: p.label, concept: p.concept, blurb: p.blurb })),
  { term: "ADSR", blurb: "Attack-Decay-Sustain-Release: the four-stage 'shape' of a note's volume over time. The heart of how a synth feels." },
  { term: "Subtractive synthesis", blurb: "Start with a harmonically-rich wave, then carve it down with a filter. The classic analog-synth approach." },
];

/* ============================================================
   Pure helpers (no audio) — these are the unit-tested bits
   ============================================================ */

export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Map a parameter value into 0..1 across its range, honoring log scaling so a
// move from 100→200 Hz counts as much as 2000→4000 Hz. Clamped to the range.
export const normalizeParam = (id, value) => {
  const p = PARAM_BY_ID[id];
  if (!p) return 0;
  const v = clamp(value, p.min, p.max);
  if (p.scale === "log") {
    const lo = Math.log(p.min);
    const hi = Math.log(p.max);
    return (Math.log(v) - lo) / (hi - lo);
  }
  return (v - p.min) / (p.max - p.min);
};

// Inverse of normalizeParam: turn a 0..1 position back into a parameter value
// (honoring log scaling). Used to synthesize "move this knob by N%" rounds.
export const denormalizeParam = (id, t) => {
  const p = PARAM_BY_ID[id];
  if (!p) return 0;
  const c = clamp(t, 0, 1);
  if (p.scale === "log") {
    const lo = Math.log(p.min);
    const hi = Math.log(p.max);
    return Math.exp(lo + c * (hi - lo));
  }
  return p.min + c * (p.max - p.min);
};

// A complete patch with every parameter at its default — the starting point.
export const defaultPatch = () =>
  PARAMS.reduce(
    (patch, p) => {
      patch[p.id] = p.default;
      return patch;
    },
    { waveform: "sawtooth" }
  );

// Convert a MIDI note number to frequency in Hz (A4 = 69 = 440 Hz).
export const noteToFreq = (midi) => 440 * 2 ** ((midi - 69) / 12);

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// Pretty name for a MIDI note, e.g. 60 → "C4" (used for the MIDI activity
// readout when an external keyboard is playing).
export const midiName = (midi) =>
  `${NOTE_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;

// Does this browser expose the Web MIDI API? (Safari historically does not.)
export const midiSupported = () =>
  typeof navigator !== "undefined" && typeof navigator.requestMIDIAccess === "function";

// Parse a raw MIDI message ([status, note, velocity]) into a note event.
// Note-on with zero velocity is treated as note-off (a common convention).
// Returns null for anything that isn't a note on/off (CC, clock, etc.).
export const parseMidiMessage = (data) => {
  if (!data || data.length < 3) return null;
  const command = data[0] & 0xf0;
  const midi = data[1];
  const velocity = data[2];
  if (command === 0x90 && velocity > 0) return { type: "on", midi, velocity };
  if (command === 0x80 || (command === 0x90 && velocity === 0)) {
    return { type: "off", midi, velocity: 0 };
  }
  return null;
};

// Parse a Control Change message ([0xB0|ch, controller, value]) into
// { controller, value }. Channel is ignored (we accept all channels). Returns
// null for non-CC messages. Used to MIDI-Learn hardware knobs (e.g. a
// MiniFreak's cutoff) and to drive on-screen knobs from incoming CC.
export const parseControlChange = (data) => {
  if (!data || data.length < 3) return null;
  if ((data[0] & 0xf0) !== 0xb0) return null;
  return { controller: data[1], value: data[2] };
};

// Map a 0..127 CC value to a parameter's value across its range, honoring the
// param's log/linear scaling so a hardware sweep feels perceptually even.
export const ccToParamValue = (id, ccValue) =>
  denormalizeParam(id, clamp(ccValue, 0, 127) / 127);

// How close a guessed patch is to a target, 0..100. Each numeric knob scores
// 1 - normalized-distance; the waveform contributes a flat match/no-match. The
// result also reports per-parameter closeness (0..1) so the UI can show which
// knobs are "warm" vs "cold". `weights` lets a mode score only some params.
export const scorePatch = (target, guess, ids = null) => {
  const scored = ids || [...PARAMS.map((p) => p.id), "waveform"];
  const perParam = {};
  let sum = 0;
  scored.forEach((id) => {
    let closeness;
    if (id === "waveform") {
      closeness = target.waveform === guess.waveform ? 1 : 0;
    } else {
      const dist = Math.abs(normalizeParam(id, target[id]) - normalizeParam(id, guess[id]));
      closeness = clamp(1 - dist, 0, 1);
    }
    perParam[id] = closeness;
    sum += closeness;
  });
  return { score: Math.round((sum / scored.length) * 100), perParam };
};

// Human label for how a single parameter changed between two patches, used by
// Name-That-Control to phrase feedback ("the attack got longer"). Returns null
// if the param is unchanged.
export const describeChange = (from, to, id) => {
  if (id === "waveform") {
    if (from.waveform === to.waveform) return null;
    const w = WAVEFORMS.find((x) => x.value === to.waveform);
    return `the waveform changed to ${w ? w.label.toLowerCase() : to.waveform}`;
  }
  const p = PARAM_BY_ID[id];
  if (!p || from[id] === to[id]) return null;
  const up = to[id] > from[id];
  const phrases = {
    cutoff: up ? "the filter opened up (brighter)" : "the filter closed down (darker)",
    resonance: up ? "the resonance increased (more peaky)" : "the resonance decreased (smoother)",
    attack: up ? "the attack got slower (softer onset)" : "the attack got faster (more percussive)",
    decay: up ? "the decay got longer" : "the decay got shorter",
    sustain: up ? "the sustain level rose" : "the sustain level dropped",
    release: up ? "the release got longer (more tail)" : "the release got shorter (more abrupt)",
  };
  return phrases[id] || `${p.label.toLowerCase()} ${up ? "increased" : "decreased"}`;
};

/* ============================================================
   Presets — playable characters + Match-mode target pool
   ============================================================ */

// Whimsically-named patches. They double as Free-Play starting points and as
// the pool of targets for Match-the-Sound. Each is a full patch plus flavor.
export const PRESETS = [
  {
    id: "dreamy-strings",
    name: "Dreamy Strings",
    emoji: "🌙",
    blurb: "Slow swell, soft and wide — the sound of staring out a rainy window.",
    patch: { waveform: "sawtooth", cutoff: 2200, resonance: 1.2, attack: 0.6, decay: 0.8, sustain: 0.85, release: 1.6 },
  },
  {
    id: "pluck-goblin",
    name: "Pluck Goblin",
    emoji: "🪕",
    blurb: "Tiny attack, fast decay, no sustain — a cheeky little plink.",
    patch: { waveform: "triangle", cutoff: 3200, resonance: 2, attack: 0.002, decay: 0.18, sustain: 0, release: 0.12 },
  },
  {
    id: "wobble-bass",
    name: "Wobble Bass",
    emoji: "🫨",
    blurb: "Dark, resonant and chunky — keeps it low and rude.",
    patch: { waveform: "square", cutoff: 420, resonance: 8, attack: 0.005, decay: 0.3, sustain: 0.6, release: 0.2 },
  },
  {
    id: "laser-cat",
    name: "Laser Cat",
    emoji: "🐱",
    blurb: "Bright saw with a whistly resonant peak. Pew pew, meow.",
    patch: { waveform: "sawtooth", cutoff: 5200, resonance: 12, attack: 0.001, decay: 0.4, sustain: 0.3, release: 0.25 },
  },
  {
    id: "glass-bell",
    name: "Glass Bell",
    emoji: "🔔",
    blurb: "Pure sine, quick ping, long shimmering tail.",
    patch: { waveform: "sine", cutoff: 6000, resonance: 1, attack: 0.001, decay: 0.9, sustain: 0.15, release: 2.4 },
  },
  {
    id: "warm-pad",
    name: "Warm Pad",
    emoji: "☁️",
    blurb: "Mellow, breathing, gently filtered — a cozy bed of sound.",
    patch: { waveform: "triangle", cutoff: 1400, resonance: 1.5, attack: 0.5, decay: 0.6, sustain: 0.9, release: 1.2 },
  },
];

/* ============================================================
   One-Knob Challenges — lock everything but one control
   ============================================================ */

// Each challenge fixes a base patch, then asks the player to move a SINGLE
// parameter toward a goal phrased as a concept. `targetValue` + `tolerance`
// (in normalized 0..1 units) define the success window. `hint` nudges the
// direction. The page locks every knob except `param`.
export const CHALLENGES = [
  {
    id: "pluckier",
    param: "attack",
    prompt: "Make it pluckier",
    concept: "Shorten the attack so the note jumps in instantly instead of swelling.",
    hint: "A percussive sound starts NOW — pull the attack way down.",
    base: { waveform: "triangle", cutoff: 3000, resonance: 2, attack: 0.8, decay: 0.3, sustain: 0.4, release: 0.3 },
    targetValue: 0.01,
    tolerance: 0.12,
  },
  {
    id: "brighter",
    param: "cutoff",
    prompt: "Make it brighter",
    concept: "Open the filter so more high harmonics come through.",
    hint: "Brightness lives up high — raise the cutoff.",
    base: { waveform: "sawtooth", cutoff: 500, resonance: 1.5, attack: 0.02, decay: 0.3, sustain: 0.7, release: 0.3 },
    targetValue: 6000,
    tolerance: 0.12,
  },
  {
    id: "longer-tail",
    param: "release",
    prompt: "Give it a long tail",
    concept: "Lengthen the release so the note rings out after you let go.",
    hint: "The tail is the release — stretch it out.",
    base: { waveform: "sine", cutoff: 4000, resonance: 1, attack: 0.01, decay: 0.4, sustain: 0.5, release: 0.05 },
    targetValue: 2.5,
    tolerance: 0.12,
  },
  {
    id: "more-vowel",
    param: "resonance",
    prompt: "Make it whistle / sing",
    concept: "Boost resonance for a peaky, vowel-like emphasis at the cutoff.",
    hint: "That whistly peak is resonance — turn it up.",
    base: { waveform: "sawtooth", cutoff: 1500, resonance: 0.7, attack: 0.02, decay: 0.3, sustain: 0.7, release: 0.3 },
    targetValue: 14,
    tolerance: 0.15,
  },
  {
    id: "dies-away",
    param: "sustain",
    prompt: "Let it die away while held",
    concept: "Drop the sustain so the note fades even while the key is down.",
    hint: "Sustain is the held level — bring it to the floor.",
    base: { waveform: "triangle", cutoff: 2500, resonance: 1.5, attack: 0.01, decay: 0.5, sustain: 1, release: 0.3 },
    targetValue: 0,
    tolerance: 0.12,
  },
];

// Whether the player's value for a challenge is within the success window.
export const challengeSolved = (challenge, value) =>
  Math.abs(normalizeParam(challenge.param, value) - normalizeParam(challenge.param, challenge.targetValue)) <=
  challenge.tolerance;

// For feedback: is the current value below ("up" = turn it higher), above
// ("down"), or within tolerance ("there") of the challenge target?
export const challengeDirection = (challenge, value) => {
  if (challengeSolved(challenge, value)) return "there";
  return normalizeParam(challenge.param, value) < normalizeParam(challenge.param, challenge.targetValue)
    ? "up"
    : "down";
};

/* ============================================================
   Randomization — fresh variety for the practice modes
   ============================================================ */

// A pseudo-random patch: random waveform + each knob at a random position in
// its (scaled) range. `rng` defaults to Math.random but is injectable so the
// generators stay testable. Used for Match-the-Sound variety + challenge bases.
export const randomPatch = (rng = Math.random) => {
  const patch = { waveform: WAVEFORM_VALUES[Math.floor(rng() * WAVEFORM_VALUES.length)] };
  PARAMS.forEach((p) => {
    patch[p.id] = denormalizeParam(p.id, rng());
  });
  return patch;
};

// Concept templates for One-Knob Challenges. Each names a single param and a
// `goal` end ("min"/"max"); makeChallenge() turns one into a concrete round
// with a randomized base patch and target, so attempts don't become memorized.
export const CHALLENGE_TEMPLATES = [
  { id: "pluckier", param: "attack", goal: "min", prompt: "Make it pluckier", concept: "Shorten the attack so the note jumps in instantly instead of swelling.", hint: "A percussive sound starts NOW — pull the attack way down." },
  { id: "swell", param: "attack", goal: "max", prompt: "Make it swell in slowly", concept: "Lengthen the attack so the note fades in like a bowed string.", hint: "A slow fade-in means a long attack." },
  { id: "brighter", param: "cutoff", goal: "max", prompt: "Make it brighter", concept: "Open the filter so more high harmonics come through.", hint: "Brightness lives up high — raise the cutoff." },
  { id: "darker", param: "cutoff", goal: "min", prompt: "Make it dark & muffled", concept: "Close the filter so the highs are rolled off.", hint: "Muffled = filter closed down — lower the cutoff." },
  { id: "whistle", param: "resonance", goal: "max", prompt: "Make it whistle / sing", concept: "Boost resonance for a peaky, vowel-like emphasis at the cutoff.", hint: "That whistly peak is resonance — turn it up." },
  { id: "longer-tail", param: "release", goal: "max", prompt: "Give it a long tail", concept: "Lengthen the release so the note rings out after you let go.", hint: "The tail is the release — stretch it out." },
  { id: "abrupt", param: "release", goal: "min", prompt: "Make it stop abruptly", concept: "Shorten the release so the note cuts off the moment you let go.", hint: "No lingering tail = short release." },
  { id: "dies-away", param: "sustain", goal: "min", prompt: "Let it die away while held", concept: "Drop the sustain so the note fades even while the key is down.", hint: "Sustain is the held level — bring it to the floor." },
  { id: "hold-steady", param: "sustain", goal: "max", prompt: "Make it hold steady", concept: "Raise the sustain so the note stays at full level while held.", hint: "A steady held note wants high sustain." },
  { id: "snappy", param: "decay", goal: "min", prompt: "Make it snap down fast", concept: "Shorten the decay so it drops to the sustain level quickly after the hit.", hint: "A fast snap after the attack = short decay." },
];

// Build a concrete, randomized challenge from a template: a random base patch
// with the target knob parked at the OPPOSITE end (so there's a clear move to
// make), and a target sampled in the goal end-band.
export const makeChallenge = (template, rng = Math.random) => {
  const { param, goal } = template;
  const base = randomPatch(rng);
  const startNorm = goal === "max" ? 0.05 + rng() * 0.2 : 0.75 + rng() * 0.2;
  base[param] = denormalizeParam(param, startNorm);
  const targetNorm = goal === "max" ? 0.88 + rng() * 0.1 : rng() * 0.1;
  return { ...template, base, targetValue: denormalizeParam(param, targetNorm), tolerance: 0.13 };
};

// A fully random challenge (random concept + random instance).
export const randomChallenge = (rng = Math.random) =>
  makeChallenge(CHALLENGE_TEMPLATES[Math.floor(rng() * CHALLENGE_TEMPLATES.length)], rng);

/* ============================================================
   Envelope shape — for the ADSR diagram (pure, no audio)
   ============================================================ */

// The diagram lays each ADSR stage into its own fixed horizontal band, so a
// long release can't squash the attack/decay into a sliver (which is what
// happened with a raw real-time x-axis). Within its band, a stage's handle
// position reflects that param's NORMALIZED value (so it tracks the knob, log
// scaling and all). The bands sum to 1 across the drawing width.
export const ENV_BANDS = { attack: 0.3, decay: 0.3, sustain: 0.12, release: 0.28 };
const ENV_PAD_Y = 0.12; // vertical headroom (fraction) so handles aren't clipped

// Level (0..1, 1 = full) → drawn y (0 = top, 1 = bottom) and back.
const yOfLevel = (level) => ENV_PAD_Y + (1 - clamp(level, 0, 1)) * (1 - 2 * ENV_PAD_Y);
const levelOfY = (y) =>
  clamp(1 - (clamp(y, ENV_PAD_Y, 1 - ENV_PAD_Y) - ENV_PAD_Y) / (1 - 2 * ENV_PAD_Y), 0, 1);

// Lay out the ADSR envelope in normalized 0..1 space: the polyline `points`
// (key-down → release) plus draggable `handles`. x grows left→right across the
// stage bands; y is 0 (top) … 1 (bottom). Pure + deterministic, so the diagram
// updates live from the knobs and is unit-testable.
export const envelopeLayout = (patch) => {
  const { attack: aW, decay: dW, sustain: sW, release: rW } = ENV_BANDS;
  const nA = normalizeParam("attack", patch.attack);
  const nD = normalizeParam("decay", patch.decay);
  const nR = normalizeParam("release", patch.release);
  const s = clamp(patch.sustain, 0, 1);

  const xAttack = nA * aW;
  const xDecay = aW + nD * dW;
  const xHoldEnd = aW + dW + sW;
  const xRelease = xHoldEnd + nR * rW;

  const points = [
    { x: 0, y: yOfLevel(0) },
    { x: xAttack, y: yOfLevel(1) },
    { x: xDecay, y: yOfLevel(s) },
    { x: xHoldEnd, y: yOfLevel(s) },
    { x: xRelease, y: yOfLevel(0) },
  ];
  return {
    points,
    handles: {
      attack: { x: xAttack, y: yOfLevel(1) }, // drag x → attack
      sustain: { x: xDecay, y: yOfLevel(s) }, // drag x → decay, y → sustain
      release: { x: xRelease, y: yOfLevel(0) }, // drag x → release
    },
  };
};

// Inverse of the layout: given a handle and a normalized pointer position
// (nx, ny in 0..1), return the patch param updates it implies. Clamped to each
// stage's band so a handle can't escape its lane.
export const envelopeDragToPatch = (handle, nx, ny) => {
  const { attack: aW, decay: dW, sustain: sW } = ENV_BANDS;
  const x = clamp(nx, 0, 1);
  if (handle === "attack") {
    return { attack: denormalizeParam("attack", clamp(x / aW, 0, 1)) };
  }
  if (handle === "sustain") {
    return {
      decay: denormalizeParam("decay", clamp((x - aW) / dW, 0, 1)),
      sustain: levelOfY(ny),
    };
  }
  if (handle === "release") {
    return { release: denormalizeParam("release", clamp((x - (aW + dW + sW)) / ENV_BANDS.release, 0, 1)) };
  }
  return {};
};

/* ============================================================
   Lessons — the /synth-lab/learn teaching content
   ============================================================ */

// A neutral patch the lesson demos vary one parameter at a time from, so each
// example isolates a single concept by ear.
const DEMO = { waveform: "sawtooth", cutoff: 2200, resonance: 1.5, attack: 0.01, decay: 0.3, sustain: 0.7, release: 0.4 };
const demo = (over) => ({ ...DEMO, ...over });

// Structured teaching content for the learning page. Each lesson has prose plus
// playable A/B/C examples that differ in just the parameter being taught.
export const LESSONS = [
  {
    id: "waveform",
    viz: "wave",
    title: "Waveform",
    tagline: "the raw tone, before anything else",
    body: "Sound starts at the oscillator. Its waveform sets how rich the raw tone is — how many harmonics (overtones) sit on top of the root pitch. A sine has none (pure), a saw has all of them (bright and buzzy). Everything else you do just shapes this starting tone.",
    listen: "Play these on the same note — notice how much 'buzz' each one has.",
    examples: [
      { label: "Sine — pure", patch: demo({ waveform: "sine" }) },
      { label: "Triangle — soft", patch: demo({ waveform: "triangle" }) },
      { label: "Square — hollow", patch: demo({ waveform: "square" }) },
      { label: "Saw — bright", patch: demo({ waveform: "sawtooth" }) },
    ],
  },
  {
    id: "cutoff",
    title: "Filter cutoff",
    tagline: "the brightness dial",
    body: "A lowpass filter lets low frequencies through and cuts the highs above its cutoff point. Lowering the cutoff rolls off the bright harmonics, so the sound gets darker and more muffled; raising it opens the sound up. This is the single most-used control for shaping a synth tone.",
    listen: "Same saw wave, three cutoff settings — dark to bright.",
    examples: [
      { label: "Low — muffled", patch: demo({ cutoff: 400 }) },
      { label: "Mid", patch: demo({ cutoff: 1500 }) },
      { label: "High — open", patch: demo({ cutoff: 7000 }) },
    ],
  },
  {
    id: "resonance",
    title: "Resonance",
    tagline: "emphasis at the cutoff",
    body: "Resonance (sometimes called Q) boosts a narrow band right at the cutoff frequency. A little adds character; a lot creates a whistly, vowel-like peak that can almost sing on its own. Sweep the cutoff with high resonance and you get that classic 'wah' synth sound.",
    listen: "Same patch, rising resonance — listen for the peak getting sharper.",
    examples: [
      { label: "None", patch: demo({ cutoff: 1200, resonance: 0.7 }) },
      { label: "Some", patch: demo({ cutoff: 1200, resonance: 6 }) },
      { label: "Lots — whistly", patch: demo({ cutoff: 1200, resonance: 16 }) },
    ],
  },
  {
    id: "attack",
    viz: "envelope",
    title: "Attack",
    tagline: "how the note begins",
    body: "Attack is the first stage of the amplitude envelope: how long the note takes to rise from silence to full volume when you press a key. A tiny attack makes a percussive, plucky onset; a long attack swells in gently, like bowing a string or a pad fading up.",
    listen: "From an instant pluck to a slow swell.",
    examples: [
      { label: "Instant", patch: demo({ attack: 0.002, sustain: 0.8 }) },
      { label: "Medium", patch: demo({ attack: 0.25, sustain: 0.8 }) },
      { label: "Slow swell", patch: demo({ attack: 1.2, sustain: 0.8 }) },
    ],
  },
  {
    id: "decay",
    viz: "envelope",
    title: "Decay & sustain",
    tagline: "the drop, and where it lands",
    body: "After the attack peak, decay is how fast the note falls to the sustain level — the volume it then holds at while the key stays down. Short decay with low sustain gives a plucky 'thump that fades'; high sustain holds steady like an organ. Decay and sustain work as a pair.",
    listen: "Snappy-and-fading vs. holding steady.",
    examples: [
      { label: "Snappy, fades", patch: demo({ attack: 0.002, decay: 0.18, sustain: 0.05 }) },
      { label: "Half-held", patch: demo({ attack: 0.002, decay: 0.4, sustain: 0.45 }) },
      { label: "Holds steady", patch: demo({ attack: 0.002, decay: 0.4, sustain: 0.95 }) },
    ],
  },
  {
    id: "release",
    viz: "envelope",
    title: "Release",
    tagline: "the tail after you let go",
    body: "Release is how long the note keeps sounding after you lift the key. A short release cuts off abruptly; a long release leaves a lingering tail that's great for pads, bells and anything dreamy. It's the difference between a staccato stab and a sound that rings out.",
    listen: "Abrupt cutoff vs. a long ringing tail.",
    examples: [
      { label: "Abrupt", patch: demo({ sustain: 0.7, release: 0.05 }) },
      { label: "Medium tail", patch: demo({ sustain: 0.7, release: 0.7 }) },
      { label: "Long tail", patch: demo({ sustain: 0.7, release: 2.6 }) },
    ],
  },
];

/* ============================================================
   Audio engine (native Web Audio API)
   ============================================================ */

let audioCtx;
let muted = false;

export const setMuted = (m) => {
  muted = Boolean(m);
};

// Whether this browser can synthesize tones at all (used to hide audio UI).
export const audioSupported = () =>
  typeof window !== "undefined" && Boolean(window.AudioContext || window.webkitAudioContext);

// Lazily create (and resume) a single shared AudioContext. Must be called from
// a user gesture the first time so browsers allow playback.
const getCtx = () => {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

const PEAK = 0.22; // master voice level, kept low to leave headroom

let masterGain;
let analyser;

// Lazily build the shared output bus: every voice feeds masterGain → analyser →
// destination, so an oscilloscope can tap the real post-filter output. Created
// on the first voice (i.e. after a user gesture), alongside the AudioContext.
const getMaster = (ctx) => {
  if (!masterGain) {
    masterGain = ctx.createGain();
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;
    masterGain.connect(analyser);
    analyser.connect(ctx.destination);
  }
  return masterGain;
};

// Return the shared AnalyserNode for visualization, WITHOUT creating the
// AudioContext (so a scope can poll harmlessly before any sound has played and
// just render a flat line until the first note brings the bus to life).
export const peekAnalyser = () => analyser || null;

// Build the oscillator → lowpass → amp gain chain for one voice and return the
// pieces plus the patch-derived envelope timings. Shared by playNote/startNote.
const buildVoice = (ctx, patch, midi, t0) => {
  const osc = ctx.createOscillator();
  osc.type = WAVEFORM_VALUES.includes(patch.waveform) ? patch.waveform : "sawtooth";
  osc.frequency.value = noteToFreq(midi);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = clamp(patch.cutoff, 20, 16000);
  filter.Q.value = clamp(patch.resonance, 0.0001, 30);

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, t0);

  osc.connect(filter);
  filter.connect(amp);
  amp.connect(getMaster(ctx));

  const a = Math.max(0.002, patch.attack);
  const d = Math.max(0.002, patch.decay);
  const susLevel = Math.max(0.0001, PEAK * clamp(patch.sustain, 0, 1));
  return { osc, amp, a, d, susLevel };
};

// Play a single note for a fixed gate length, scheduling the whole ADSR shape
// up front. Used for previews, target playback and tapped on-screen keys.
export const playNote = (patch, midi, gateMs = 650) => {
  const ctx = getCtx();
  if (!ctx || muted) return;
  const t0 = ctx.currentTime;
  const { osc, amp, a, d, susLevel } = buildVoice(ctx, patch, midi, t0);
  const attackEnd = t0 + a;
  const decayEnd = attackEnd + d;
  const gateEnd = Math.max(decayEnd, t0 + gateMs / 1000);
  const rel = Math.max(0.01, patch.release);

  amp.gain.exponentialRampToValueAtTime(PEAK, attackEnd);
  amp.gain.exponentialRampToValueAtTime(susLevel, decayEnd);
  amp.gain.setValueAtTime(susLevel, gateEnd);
  amp.gain.exponentialRampToValueAtTime(0.0001, gateEnd + rel);

  osc.start(t0);
  osc.stop(gateEnd + rel + 0.05);
};

// Start a held note and return a `stop()` that triggers the release. Used by
// Free Play so on-screen / typed keys can be held down and let go.
export const startNote = (patch, midi) => {
  const ctx = getCtx();
  if (!ctx || muted) return { stop: () => {} };
  const t0 = ctx.currentTime;
  const { osc, amp, a, d, susLevel } = buildVoice(ctx, patch, midi, t0);
  amp.gain.exponentialRampToValueAtTime(PEAK, t0 + a);
  amp.gain.exponentialRampToValueAtTime(susLevel, t0 + a + d);
  osc.start(t0);

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    const tr = ctx.currentTime;
    const rel = Math.max(0.01, patch.release);
    const cur = Math.max(0.0001, amp.gain.value);
    amp.gain.cancelScheduledValues(tr);
    amp.gain.setValueAtTime(cur, tr);
    amp.gain.exponentialRampToValueAtTime(0.0001, tr + rel);
    osc.stop(tr + rel + 0.05);
  };
  return { stop };
};
